import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { some } from 'lodash';
import { Checkbox, Divider, Row, Col, CheckboxProps } from 'antd';
import { CheckboxGroupProps, CheckboxValueType } from 'antd/lib/checkbox/Group';

import './styles.css';

export interface MultiCheckboxProps {
  showSelectAll?: boolean | PropPresets.StringOrElement;
  disabled?: boolean;
  initialValues?: string[] | number[];
  divider?: boolean;
  onChange?: (val: CheckboxValueType[]) => void;
  colSpan?: number;
  options: PropPresets.OptionObject[];
  groupProps?: CheckboxGroupProps;
  checkboxProps?: CheckboxProps;
}

function MultiCheckbox(props: MultiCheckboxProps) {
  const {
    showSelectAll = false,
    divider = true,
    disabled = false,
    options: propOptions,
    onChange,
    colSpan = 6,
    initialValues,
    groupProps,
    checkboxProps,
  } = props;

  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [checkedList, setCheckedList] = useState<CheckboxValueType[]>(initialValues || []);
  const [selectAllDisabled, setSelectAllDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (some(propOptions, (option) => option?.disabled)) {
      setSelectAllDisabled(true);
    }
    return setSelectAllDisabled(false);
  }, [propOptions]);

  useEffect(() => {
    const disabledValues = new Set(
      propOptions.filter(({ disabled: optionDisabled }) => optionDisabled).map(({ value }) => value),
    );
    const enabledCheckedList = checkedList.filter((value) => !disabledValues.has(value));
    if (enabledCheckedList.length === checkedList.length) return;

    setCheckedList(enabledCheckedList);
    setSelectAll(false);
    onChange?.(enabledCheckedList);
  }, [checkedList, onChange, propOptions]);

  // useEffect(() => {
  //   setSelectAll(initialValues?.length === propOptions.length);
  // }, [initialValues, propOptions]);

  const handleChange: NonNullable<CheckboxGroupProps['onChange']> = useCallback(
    (checkedListVal) => {
      onChange?.(checkedListVal);
      setCheckedList(checkedListVal);
      setSelectAll(checkedListVal.length === propOptions.length);
    },
    [propOptions, onChange],
  );

  const handleSelectAll: NonNullable<CheckboxProps['onChange']> = useCallback(
    (e) => {
      const { checked } = e.target;
      const formattedCheckedList = checked ? propOptions.map((op) => op.value) : [];
      onChange?.(formattedCheckedList);
      setCheckedList(formattedCheckedList);
      setSelectAll(checked);
    },
    [propOptions, onChange],
  );

  const CheckboxRow = useMemo(
    () => (
      <Row>
        {propOptions.map(({ label, value, disabled: optionDisabled }) => (
          <Col key={String(value)} span={colSpan} md={colSpan} xs={24}>
            <Checkbox
              value={value}
              style={{ margin: '5px 0' }}
              {...checkboxProps}
              disabled={disabled || optionDisabled || checkboxProps?.disabled}
            >
              {label}
            </Checkbox>
          </Col>
        ))}
      </Row>
    ),
    [propOptions, colSpan, checkboxProps, disabled],
  );

  return (
    <>
      {showSelectAll && (
        <>
          <Checkbox
            disabled={disabled || selectAllDisabled}
            onChange={handleSelectAll}
            checked={selectAll}
            className="multi-select-all"
          >
            {typeof showSelectAll === 'boolean' ? 'Select All' : showSelectAll}
          </Checkbox>
          {divider ? <Divider style={{ margin: '15px 0' }} /> : null}
        </>
      )}

      <Checkbox.Group
        disabled={disabled}
        onChange={handleChange}
        value={checkedList}
        style={{ width: '100%' }}
        {...groupProps}
      >
        {CheckboxRow}
      </Checkbox.Group>
    </>
  );
}

export default MultiCheckbox;
